/**
 * <s-carousel> — horizontal scroll-snap row with page-wise arrow buttons.
 *
 * Required markup:
 *   [data-carousel-track]                     — horizontally scrollable element.
 * Optional markup:
 *   [data-carousel-prev] / [data-carousel-next] — buttons; disabled at the ends.
 *   [data-carousel-progress]                  — element whose width tracks scroll progress.
 */
class SCarousel extends HTMLElement {
  #ac;
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('click', this.#handleClick, opts);
    this.track?.addEventListener('scroll', this.#scheduleUpdate, { ...opts, passive: true });
    window.addEventListener('resize', this.#scheduleUpdate, { ...opts, passive: true });

    if (this.hasAttribute('data-drag')) this.#enableDrag(opts);
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get track() {
    return this.querySelector('[data-carousel-track]');
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element) || !this.track) return;
    const prev = event.target.closest('[data-carousel-prev]');
    const next = event.target.closest('[data-carousel-next]');
    if (!prev && !next) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const direction = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    const distance = this.track.clientWidth * (next ? 1 : -1) * direction;
    this.track.scrollBy({ left: distance, behavior: reduceMotion ? 'auto' : 'smooth' });
  };


  // Opt-in (data-drag, 2 Nov 2026): hold the mouse and drag the row, or grab the progress bar ([data-carousel-scrub]) like a slider.
  #enableDrag(opts) {
    const track = this.track;
    if (!track) return;
    let dragging = false;
    let moved = false;
    let startX = 0;
    let startScroll = 0;
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      dragging = true;
      moved = false;
      startX = event.clientX;
      startScroll = track.scrollLeft;
    }, opts);
    window.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const dx = event.clientX - startX;
      if (!moved && Math.abs(dx) > 5) {
        moved = true;
        this.setAttribute('data-dragging', '');
        track.setPointerCapture?.(event.pointerId);
      }
      if (moved) track.scrollLeft = startScroll - dx;
    }, opts);
    const end = () => {
      if (!dragging) return;
      dragging = false;
      this.removeAttribute('data-dragging');
      if (moved) {
        // Let the row settle on the nearest item again.
        const left = track.scrollLeft;
        track.style.scrollSnapType = 'none';
        track.scrollLeft = left;
        requestAnimationFrame(() => { track.style.scrollSnapType = ''; });
      }
    };
    window.addEventListener('pointerup', end, opts);
    window.addEventListener('pointercancel', end, opts);
    // A drag must not open the product under the mouse.
    track.addEventListener('click', (event) => {
      if (moved) {
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      }
    }, { ...opts, capture: true });
    track.addEventListener('dragstart', (event) => event.preventDefault(), opts);

    const scrub = this.querySelector('[data-carousel-scrub]');
    if (!scrub) return;
    let scrubbing = false;
    const seek = (event) => {
      const rect = scrub.getBoundingClientRect();
      const visible = track.clientWidth / track.scrollWidth;
      const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left - (rect.width * visible) / 2) / (rect.width * (1 - visible) || 1)));
      track.scrollLeft = ratio * (track.scrollWidth - track.clientWidth);
    };
    scrub.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      scrubbing = true;
      this.setAttribute('data-dragging', '');
      scrub.setPointerCapture?.(event.pointerId);
      seek(event);
      event.preventDefault();
    }, opts);
    scrub.addEventListener('pointermove', (event) => { if (scrubbing) seek(event); }, opts);
    const stop = () => {
      if (!scrubbing) return;
      scrubbing = false;
      this.removeAttribute('data-dragging');
    };
    scrub.addEventListener('pointerup', stop, opts);
    scrub.addEventListener('pointercancel', stop, opts);
  }

  #scheduleUpdate = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const track = this.track;
    if (!track) return;

    const max = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);
    const scrollable = max > 1;

    const prev = this.querySelector('[data-carousel-prev]');
    const next = this.querySelector('[data-carousel-next]');
    if (prev) prev.disabled = !scrollable || position <= 1;
    if (next) next.disabled = !scrollable || position >= max - 1;

    this.toggleAttribute('data-scrollable', scrollable);

    const progress = this.querySelector('[data-carousel-progress]');
    if (progress) {
      const visible = scrollable ? track.clientWidth / track.scrollWidth : 1;
      const ratio = scrollable ? position / max : 0;
      progress.style.width = `${visible * 100}%`;
      progress.style.marginInlineStart = `${ratio * (1 - visible) * 100}%`;
    }
  }
}

if (!customElements.get('s-carousel')) {
  customElements.define('s-carousel', SCarousel);
}
